import { CSSProperties, useCallback, useMemo, useState, useEffect } from 'react'
import { useLocation, Link as RouterLink } from 'react-router-dom'
import { Grid, Skeleton } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import Drawer from '@mui/material/Drawer'
import Link from '@mui/material/Link'
import List from '@mui/material/List'
import { UserRole } from '@common/interfaces/user'
import cn from 'classnames'
import Logo from '../../assets/images/client-intake/dwight-black-logo.svg?react'
import { ROUTES } from '../../constants/routes'
import { ATLAS_PREVIEW_OPT_OUT } from '../../hooks/useGlobalAtlasLinkPreview'
import { useIsMobile } from '../../hooks/useIsMobile'
import styles from './SideMenuClient.module.scss'
import {
  SIDE_MENU,
  SIDE_MENU_PAST_CLIENT_USER,
  SIDE_MENU_CLIENT_USER,
  SIDE_MENU_DUE_DILIGENCE_CLIENT_USER,
} from './SideMenuConfig'
import SideMenuItems from './components/SideMenuItems'
import { PropsFromRedux } from './index'
import { getInitialExpandableListItemsState, updateExpandableListItemsState } from './utils'

type IProps = PropsFromRedux

const SideMenuClient = ({
  role = UserRole.CLIENT_USER,
  isSidebarOpen,
  isPastClient,
  isDueDiligenceClient,
  isAdminLoggedAsClient,
  clientNewsfeedData,
  listClientNewsfeed,
  toggleSidebar,
}: IProps) => {
  const location = useLocation()
  const isMobile = useIsMobile()
  const [currentSlide, setCurrentSlide] = useState(0)
  const [expandableListItemsState, setExpandableListItemsState] = useState(
    getInitialExpandableListItemsState(role, location),
  )

  // Removed: an effect that force-opened the rail on every mobile -> desktop transition.
  // It read as "make sure the permanent drawer is visible", but `isSidebarOpen` is the
  // user's *pin* preference — so rotating a tablet, or resizing a window past 960px, threw
  // away a deliberate choice to keep the rail collapsed. Nothing replaces it: the rail is
  // desktop-only now, and it renders collapsed-or-pinned from that same preference.

  useEffect(() => {
    void listClientNewsfeed()
  }, [listClientNewsfeed])

  const newsfeedItems = useMemo(() => {
    return clientNewsfeedData?.data?.data || []
  }, [clientNewsfeedData])

  const notificationContent = useMemo(() => {
    const cardThemes = [
      { colorKey: 'blue', svg: '/src/assets/images/blue-background-news.svg' },
      { colorKey: 'green', svg: '/src/assets/images/green-background-news.svg' },
      { colorKey: 'orange', svg: '/src/assets/images/orange-background-news.svg' },
    ]
    const rotations = ['rotate(8deg)', 'rotate(-5deg)', 'rotate(12deg)', 'rotate(-8deg)']

    return newsfeedItems.map((item, index) => {
      const colorIndex = index % cardThemes.length
      const { colorKey, svg } = cardThemes[colorIndex]
      return {
        description: item.newsfeedHeadlines?.headline,
        headline: item.newsfeedHeadlines?.originalBrand || item.newsfeedHeadlines?.type,
        link: item.newsfeedHeadlines?.link,
        surfaceColor: `var(--news-carousel-${colorKey}-surface)`,
        accentColor: `var(--news-carousel-${colorKey}-accent)`,
        backgroundSvg: svg,
        rotation: rotations[colorIndex],
      }
    })
  }, [newsfeedItems])

  useEffect(() => {
    if (notificationContent.length <= 1) return

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % notificationContent.length)
    }, 30000)

    return () => clearInterval(interval)
  }, [notificationContent.length])

  const handleItemExpand = useCallback(
    (itemName: string) => {
      const newState = updateExpandableListItemsState(expandableListItemsState, itemName)
      setExpandableListItemsState(newState)
    },
    [expandableListItemsState],
  )

  const handleNotificationClick = useCallback(() => {
    const link = notificationContent[currentSlide]?.link
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer')
    }
  }, [notificationContent, currentSlide])

  const renderDescription = useCallback((description: string, headline: string) => {
    const truncatedDescription =
      description.length > 85 ? description.slice(0, 85) + '...' : description

    if (!headline) return truncatedDescription

    const headlineLower = headline.toLowerCase()
    const descriptionLower = truncatedDescription.toLowerCase()
    const index = descriptionLower.indexOf(headlineLower)

    if (index === -1) return truncatedDescription

    const before = truncatedDescription.slice(0, index)
    const match = truncatedDescription.slice(index, index + headline.length)
    const after = truncatedDescription.slice(index + headline.length)

    return (
      <>
        {before}
        <span className={styles.highlightedText}>{match}</span>
        {after}
      </>
    )
  }, [])

  const sideMenuItems = useMemo(() => {
    if (role) {
      if (role === UserRole.CLIENT_USER) {
        if (isDueDiligenceClient) {
          return SIDE_MENU_DUE_DILIGENCE_CLIENT_USER
        }

        if (isPastClient) {
          return SIDE_MENU_PAST_CLIENT_USER.filter(
            (item) => !isAdminLoggedAsClient || item.link !== ROUTES.SETTINGS,
          )
        }
        if (isPastClient !== null) {
          return SIDE_MENU_CLIENT_USER.filter(
            (item) => !isAdminLoggedAsClient || item.link !== ROUTES.SETTINGS,
          )
        }
      }
      return SIDE_MENU[role]
    }
  }, [role, isPastClient, isDueDiligenceClient, isAdminLoggedAsClient])
  return (
    <>
      <Drawer
        {...ATLAS_PREVIEW_OPT_OUT}
        className={cn(styles.drawer, {
          [styles.expanded]: isSidebarOpen,
          [styles.collapsed]: !isSidebarOpen,
        })}
        variant={isMobile ? 'temporary' : 'permanent'}
        classes={{
          paper: styles.drawerPaper,
        }}
        anchor={isMobile ? 'top' : 'left'}
        open={isMobile ? isSidebarOpen : true}
        onClose={toggleSidebar}
        ModalProps={{
          keepMounted: !isMobile, // Only keep mounted on desktop
        }}
        hideBackdrop={!isMobile || (isMobile && !isSidebarOpen)}
        slotProps={{
          backdrop:
            isMobile && !isSidebarOpen
              ? undefined // Don't render backdrop at all when closed on mobile
              : { invisible: !isSidebarOpen },
        }}
      >
        <List
          component="nav"
          classes={{
            root: styles.wrapper,
          }}
        >
          <Link component={RouterLink} to={ROUTES.HOMEPAGE} className={styles.logoWrapper}>
            <Collapse
              orientation={isMobile ? 'vertical' : 'horizontal'}
              in={isSidebarOpen || isMobile}
            >
              <Logo className={styles.logo} />
            </Collapse>
          </Link>
          <div className={styles.sideMenuItems}>
            {!sideMenuItems.length ? (
              <>
                <Grid container className={styles.skeletonGrid}>
                  {Array.from({ length: 5 }).map((_, key) => (
                    <>
                      <Grid key={key} size={isSidebarOpen ? 3 : 12}>
                        <Skeleton
                          variant="rectangular"
                          width={30}
                          height={30}
                          className={styles.skeleton}
                        />
                      </Grid>
                      {isSidebarOpen && (
                        <Grid key={key} size={9}>
                          <Skeleton
                            variant="rectangular"
                            width={150}
                            height={30}
                            className={styles.skeleton}
                          />
                        </Grid>
                      )}
                    </>
                  ))}
                </Grid>
              </>
            ) : (
              <SideMenuItems
                sideMenuItems={sideMenuItems}
                isSidebarOpen={isSidebarOpen}
                expandableListItemsState={expandableListItemsState}
                handleItemExpand={handleItemExpand}
                toggleSidebar={isMobile ? toggleSidebar : undefined}
              />
            )}
          </div>

          {(isSidebarOpen || isMobile) && notificationContent.length > 0 && (
            <div className={styles.notificationsSection}>
              <div
                className={cn(styles.notificationModal, {
                  [styles.notificationModalClickable]: !!notificationContent[currentSlide].link,
                })}
                style={
                  {
                    '--card-surface': notificationContent[currentSlide].surfaceColor,
                    '--card-accent': notificationContent[currentSlide].accentColor,
                    '--background-svg': `url("${notificationContent[currentSlide].backgroundSvg}")`,
                  } as CSSProperties
                }
                onClick={handleNotificationClick}
              >
                <div className={styles.notificationContent}>
                  {notificationContent[currentSlide].headline && (
                    <span className={styles.newsHeadline}>
                      {notificationContent[currentSlide].headline}
                    </span>
                  )}
                  <span className={styles.contentDescription}>
                    {renderDescription(
                      notificationContent[currentSlide].description,
                      notificationContent[currentSlide].headline,
                    )}
                  </span>
                </div>
              </div>

              <div className={styles.carouselDots}>
                {notificationContent.map((_, index) => (
                  <button
                    key={index}
                    className={cn(styles.dot, {
                      [styles.dotActive]: index === currentSlide,
                    })}
                    onClick={() => setCurrentSlide(index)}
                  />
                ))}
              </div>
            </div>
          )}
        </List>
      </Drawer>
    </>
  )
}

export default SideMenuClient
